import React from 'react';
import { Plus, Trash2, FileSignature, TrendingUp, Download, Search, X } from 'lucide-react';
import { projectService } from '../services/projectService';
import { DataTable } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { useOverviewTables } from '../hooks/useOverviewTables';
import { TEAM_COLUMNS, TASK_COLUMNS, TRANSACTION_COLUMNS } from '../constants/overviewTableConstants';
import { MemberTransactionsModal } from './MemberTransactionsModal';
export { TeamAssignmentForm } from './add_member';

const memberId = (fo) => (typeof fo === 'object' ? fo._id : fo);
const memberIds = (project) => (project.fieldOfficers?.map((fo) => String(memberId(fo))) || []);


export const OverviewActivityTables = ({ 
  project, 
  formatCurrency, 
  onUpdate, 
  teamActionsLocked = false,
  hideTransactions = false,
  hideTasks = false,
  onAddMemberClick,
  onEditMemberClick,
  onRequestExpense
}) => {
  const { tasks, transactionRows, formatRowDate } = useOverviewTables(project);
  
  const [viewMemberTxs, setViewMemberTxs] = React.useState(null);

  const getMemberTransactions = (member) => {
    const txs = project?.paymentSummary?.transactions || [];
    return txs.filter(t => {
      if (t.kind !== 'expense') return false;
      const uId = t.requestedBy?._id?.toString() || t.requestedBy?.id?.toString() || (typeof t.requestedBy === 'string' ? t.requestedBy : null) || t.createdBy?._id?.toString() || (typeof t.createdBy === 'string' ? t.createdBy : null);
      if (!uId || uId !== member._id?.toString()) return false;
      return t.requestStatus === 'paid' || t.requestStatus === 'approved' || t.requestStatus === 'partial_approved' || t.requestStatus === 'pending';
    });
  };

  const handleViewTransactions = (member) => {
    setViewMemberTxs(member);
  };

  const handleExportTransactions = (member) => {
    const txs = getMemberTransactions(member);
    if (!txs.length) {
      alert("This member has no approved expense transactions to export.");
      return;
    }
    
    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '""';
      if (typeof val === 'number') return val;
      const str = String(val).replace(/"/g, '""').replace(/\r?\n|\r/g, ' ');
      return `"${str}"`;
    };

    const headers = ['Transaction ID', 'Member Name', 'Client Name', 'Project ID', 'Customer Project ID', 'Site Name', 'Customer', 'Project Status', 'PO Value', 'Budget', 'Location', 'Date', 'Category', 'Reason', 'Status', 'Requested Amount', 'Approved Amount', 'Disbursed Amount', 'Payment Method', 'Ref ID', 'Bank Name', 'UPI/Phone', 'Receiver Name', 'Cheque Number'];
    const rows = txs.map(t => [
      escapeCsv(t.transactionId || t.txnId || (t._id ? `TXN-${String(t._id).slice(-6).toUpperCase()}` : '')),
      escapeCsv(member?.name || ''),
      escapeCsv(project?.customerName || ''),
      escapeCsv(project?.projectId || ''),
      escapeCsv(project?.applicationId || ''),
      escapeCsv(project?.siteName || ''),
      escapeCsv(project?.projectName || ''),
      escapeCsv(project?.status || ''),
      escapeCsv(project?.projectValue || 0),
      escapeCsv(project?.targetCost || 0),
      escapeCsv([project?.address?.city, project?.address?.state].filter(Boolean).join(', ') || ''),
      escapeCsv(t.transactionDate ? new Date(t.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
      escapeCsv(t.categoryLabel || t.expenseCategory || ''),
      escapeCsv(t.expenseReason || ''),
      escapeCsv(t.requestStatus || ''),
      escapeCsv(t.amount || 0),
      escapeCsv(t.approvedAmount || 0),
      escapeCsv(t.disbursedAmount || 0),
      escapeCsv(t.paymentMethod || ''),
      escapeCsv(t.refId || ''),
      escapeCsv(t.bankName || ''),
      escapeCsv(t.upiPhone || ''),
      escapeCsv(t.receiverName || ''),
      escapeCsv(t.chequeNumber || '')
    ].join(','));
    
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${member.name || 'Member'}_transactions.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportAllTransactions = () => {
    if (!project?.fieldOfficers?.length) {
      alert("No team members assigned to export.");
      return;
    }
    
    let allTxs = [];
    for (const member of project.fieldOfficers) {
      if (typeof member !== 'object') continue;
      const txs = getMemberTransactions(member);
      allTxs = allTxs.concat(txs.map(t => ({ ...t, _member: member })));
    }
    
    if (!allTxs.length) {
      alert("There are no approved expense transactions for any team member yet.");
      return;
    }

    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '""';
      if (typeof val === 'number') return val;
      const str = String(val).replace(/"/g, '""').replace(/\r?\n|\r/g, ' ');
      return `"${str}"`;
    };

    const headers = ['Transaction ID', 'Member Name', 'Client Name', 'Project ID', 'Customer Project ID', 'Site Name', 'Customer', 'Project Status', 'PO Value', 'Budget', 'Location', 'Date', 'Category', 'Reason', 'Status', 'Requested Amount', 'Approved Amount', 'Disbursed Amount', 'Payment Method', 'Ref ID', 'Bank Name', 'UPI/Phone', 'Receiver Name', 'Cheque Number'];
    const rows = allTxs.map(t => [
      escapeCsv(t.transactionId || t.txnId || (t._id ? `TXN-${String(t._id).slice(-6).toUpperCase()}` : '')),
      escapeCsv(t._member?.name || ''),
      escapeCsv(project?.customerName || ''),
      escapeCsv(project?.projectId || ''),
      escapeCsv(project?.applicationId || ''),
      escapeCsv(project?.siteName || ''),
      escapeCsv(project?.projectName || ''),
      escapeCsv(project?.status || ''),
      escapeCsv(project?.projectValue || 0),
      escapeCsv(project?.targetCost || 0),
      escapeCsv([project?.address?.city, project?.address?.state].filter(Boolean).join(', ') || ''),
      escapeCsv(t.transactionDate ? new Date(t.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
      escapeCsv(t.categoryLabel || t.expenseCategory || ''),
      escapeCsv(t.expenseReason || ''),
      escapeCsv(t.requestStatus || ''),
      escapeCsv(t.amount || 0),
      escapeCsv(t.approvedAmount || 0),
      escapeCsv(t.disbursedAmount || 0),
      escapeCsv(t.paymentMethod || ''),
      escapeCsv(t.refId || ''),
      escapeCsv(t.bankName || ''),
      escapeCsv(t.upiPhone || ''),
      escapeCsv(t.receiverName || ''),
      escapeCsv(t.chequeNumber || '')
    ].join(','));
    
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${project?.projectId || 'Project'}_all_team_transactions.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteMember = async (officer) => {
    if (teamActionsLocked) return;
    const currentIds = project.fieldOfficers.map((fo) => memberId(fo));
    const newIds = currentIds.filter((id) => String(id) !== String(officer._id));
    await projectService.updateProject(project._id, { fieldOfficers: newIds });
    if (onUpdate) onUpdate(await projectService.getProjectById(project._id));
  };

  const expensesMap = React.useMemo(() => {
    const map = {};
    const txs = project?.paymentSummary?.transactions || [];
    for (const t of txs) {
      if (t.kind === 'expense') {
        const uId = t.requestedBy?._id?.toString() || t.requestedBy?.id?.toString() || (typeof t.requestedBy === 'string' ? t.requestedBy : null) || t.createdBy?._id?.toString() || (typeof t.createdBy === 'string' ? t.createdBy : null);
        if (uId) {
          if (t.requestStatus === 'paid' || t.requestStatus === 'approved' || t.requestStatus === 'partial_approved') {
            const reqAmt = Number(t.amount) || 1;
            const booked = Number(t.approvedAmount ?? t.amount) || 0;
            const ratio = booked / reqAmt;
            const std = (Number(t.standardAmount) || 0) * ratio;
            const ep = (Number(t.epAmount) || 0) * ratio;
            const ns = (Number(t.nonStandardAmount) || 0) * ratio;
            const oth = (Number(t.othersAmount) || 0) * ratio;
            const tradeSum = std + ep + ns + oth;
            
            let applicable = booked;
            if (tradeSum > 0.01) {
              applicable = Math.max(0, booked - tradeSum);
            }
            map[uId] = (map[uId] || 0) + applicable;
          }
        }
      }
    }
    return map;
  }, [project?.paymentSummary?.transactions]);

  return (
    <div className="w-full flex flex-col gap-4">
      <div id="team-management" className="bg-white border border-slate-100 rounded-xl overflow-hidden flex flex-col scroll-mt-24">
        <div className="p-4 border-b border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleExportAllTransactions}
            className="flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold tracking-wider transition-all shadow-sm bg-transparent border border-emerald-600 text-emerald-600 hover:bg-emerald-50"
          >
            <Download className="w-3.5 h-3.5" />
            Export All
          </button>
          <button
            type="button"
            onClick={onAddMemberClick}
            disabled={teamActionsLocked}
            className={`flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold tracking-wider transition-all shadow-sm ${
              teamActionsLocked ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' : 'bg-transparent border border-[#2b448f] text-[#2b448f] hover:bg-blue-50'
            }`}
          >
            <Plus className="w-3.5 h-3.5" />
            {project?.fieldOfficers?.length > 0 ? 'Update Member' : 'Add Member'}
          </button>
        </div>

        <DataTable 
          columns={TEAM_COLUMNS(handleDeleteMember, teamActionsLocked, expensesMap, formatCurrency, handleViewTransactions, handleExportTransactions, onRequestExpense, project?.projectId, onEditMemberClick)}
          data={project?.fieldOfficers || []}
          noScroll={true}
        />
      </div>

      {!hideTasks && project?.advancePaymentStatus === 'Approved' && (
        <div className="bg-white border border-slate-100 rounded-xl overflow-hidden flex flex-col mt-4">
          <div className="p-6 border-b border-slate-100 flex items-center gap-3">
            <FileSignature className="w-5 h-5 text-[#2b448f]" />
            <h3 className="text-[13px] font-black text-slate-800 tracking-tight">Task assignments</h3>
          </div>
          <DataTable 
            columns={TASK_COLUMNS}
            data={tasks}
            noScroll={true}
          />
        </div>
      )}

      {!hideTransactions && (
        <div className="bg-white border border-slate-100 rounded-xl overflow-hidden flex flex-col mt-4">
          <div className="p-6 border-b border-slate-100 flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            <h3 className="text-[13px] font-black text-slate-800 tracking-tight">Transaction history</h3>
          </div>
          <DataTable 
            columns={TRANSACTION_COLUMNS(formatCurrency, formatRowDate)}
            data={transactionRows}
            noScroll={true}
          />
        </div>
      )}

      {viewMemberTxs && (
        <MemberTransactionsModal
          member={viewMemberTxs}
          project={project}
          transactions={getMemberTransactions(viewMemberTxs)}
          onClose={() => setViewMemberTxs(null)}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};

